import momentTz from 'moment-timezone';

/**
 * Reason categories captured when a specialist changes an account's will-renew prediction
 * (issue 18023). Values are serialized as the enum string (e.g. 'Price') and sent to the API
 * verbatim. Categories are grouped by the direction of the prediction change:
 *  - Churn / no-renewal categories surface when flipping the prediction to "No".
 *  - Positive / recovery categories surface on the "No" -> "Yes" recovery flip.
 */
export enum WillRenewPredictionReasonCategory {
	NeverGotStarted = 'NeverGotStarted',
	DidntSeeValue = 'DidntSeeValue',
	UseCase = 'UseCase',
	Issues = 'Issues',
	Adoption = 'Adoption',
	Price = 'Price',
	Competitor = 'Competitor',
	Concerns = 'Concerns',
	Product = 'Product',
	BusinessChanges = 'BusinessChanges',
	Discount = 'Discount',
	HRPCSave = 'HRPCSave',
	Other = 'Other',
}

const CATEGORY_TITLES: Record<WillRenewPredictionReasonCategory, string> = {
	[WillRenewPredictionReasonCategory.NeverGotStarted]: 'Never got started',
	[WillRenewPredictionReasonCategory.DidntSeeValue]: "Didn't see value",
	[WillRenewPredictionReasonCategory.UseCase]: 'Use case',
	[WillRenewPredictionReasonCategory.Issues]: 'Issues',
	[WillRenewPredictionReasonCategory.Adoption]: 'Adoption',
	[WillRenewPredictionReasonCategory.Price]: 'Price',
	[WillRenewPredictionReasonCategory.Competitor]: 'Competitor',
	[WillRenewPredictionReasonCategory.Concerns]: 'Concerns',
	[WillRenewPredictionReasonCategory.Product]: 'Product',
	[WillRenewPredictionReasonCategory.BusinessChanges]: 'Business changes',
	[WillRenewPredictionReasonCategory.Discount]: 'Discount',
	[WillRenewPredictionReasonCategory.HRPCSave]: 'HRPC save',
	[WillRenewPredictionReasonCategory.Other]: 'Other',
};

/** Categories shown when flipping the prediction to "No". */
export const CHURN_REASON_CATEGORIES: WillRenewPredictionReasonCategory[] = [
	WillRenewPredictionReasonCategory.NeverGotStarted,
	WillRenewPredictionReasonCategory.DidntSeeValue,
	WillRenewPredictionReasonCategory.UseCase,
	WillRenewPredictionReasonCategory.Issues,
	WillRenewPredictionReasonCategory.Adoption,
	WillRenewPredictionReasonCategory.Price,
	WillRenewPredictionReasonCategory.Competitor,
	WillRenewPredictionReasonCategory.Concerns,
	WillRenewPredictionReasonCategory.Product,
	WillRenewPredictionReasonCategory.BusinessChanges,
];

/** Categories shown on the "No" -> "Yes" recovery flip. */
export const POSITIVE_REASON_CATEGORIES: WillRenewPredictionReasonCategory[] = [
	WillRenewPredictionReasonCategory.Discount,
	WillRenewPredictionReasonCategory.HRPCSave,
	WillRenewPredictionReasonCategory.Other,
];

/**
 * Human-readable title for a stored reason category string. Falls back to the raw value for
 * unknown categories so older/server-only values still render something sensible.
 */
export const getWillRenewPredictionReasonCategoryTitle = (category?: string): string => {
	if (!category) {
		return '';
	}
	return CATEGORY_TITLES[category as WillRenewPredictionReasonCategory] ?? category;
};

/** Formats a reason timestamp in Eastern Time (e.g. "May 31, 2026 3:02 PM EDT"). */
export const formatPredictionReasonTimestamp = (date?: Date | string): string => {
	if (!date) {
		return '';
	}
	const m = momentTz(date).tz('America/New_York');
	return m.isValid() ? m.format('MMM D, YYYY h:mm A z') : '';
};
